import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { trackFilter, trackQuickFilter } from "@/events/evolutionsMixpanel";
import useDebounce from "@/hooks/useDebounce";
import { useGoBack } from "@/hooks/useGoBack";
import {
  quickFilterKeyFromType,
  useMyClubFilter,
} from "@/hooks/useMyClubFilter";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import { ToolbarAction } from "@/pages/EvolutionsHub/EvolutionsPage/EvolutionsPage";
import { EvolutionsPlayersSection } from "@/pages/EvolutionsHub/sections/EvolutionsPlayersSection";
import {
  PlayersViewToggle,
  usePlayersView,
} from "@/pages/Players/PlayersViewToggle";
import { QuickFilters } from "@/pages/Players/QuickFilters";
import {
  addPlayerToPlayStylesLab,
  getCurrentPlayStylesLabPlayerSelector,
} from "@/redux/playStylesLab/playStylesLabSlice";
import {
  MetaRatingsPlayer,
  PlayerRole,
  clearPlayers,
  fetchPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import type { PlayersSortByType } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

export const PlayStylesLabPlayerPicker = () => {
  const { resourceId } = useParams();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: isTouchDevice ? 80 : 200,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const { t } = useTranslation(["players", "common"]);
  const [playerRole, setPlayerRole] = useState<PlayerRole>();
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm, errorMessage: searchTermErrorMessage } =
    useDebounce(searchTerm, {
      minLength: 3,
      contextForErrorMessage: t("common:playerName"),
    });
  const [elementOpened, setElementOpened] = useState<ToolbarAction>("none");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PlayersSortByType>("evo-popularity");
  const sortBeforeSearchRef = useRef<PlayersSortByType | null>(null);

  const refPlayerRole = useOutsideClick(() => {
    if (elementOpened === "playerRole") setElementOpened("none");
  });
  const refFilters = useOutsideClick(() => {
    if (elementOpened === "filters") setElementOpened("none");
  });

  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const currentPlayStylesLabPlayer = useSelector(
    getCurrentPlayStylesLabPlayerSelector,
  );
  const stableQueryParams = useSelector(playerFiltersAsQueryParamsSelector);

  const {
    players,
    totalPages,
    playersStatus,
    playerFilters,
    playerRoles,
    requestedFromPage,
  } = useSelector(playersSelector);

  const dispatch = useDispatch<AppDispatch>();

  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );

  useEffect(() => {
    if (
      debouncedSearchTerm &&
      sort === "evo-popularity" &&
      sortBeforeSearchRef.current === null
    ) {
      return;
    }

    if (!debouncedSearchTerm && sortBeforeSearchRef.current !== null) {
      return;
    }

    dispatch(
      fetchPlayers({
        page,
        sort: sort,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        activeSquad: ownedOrEvolvedFilters?.category === "ActiveSquad",
        playerRoleId: playerRole?.id,
        queryParams: stableQueryParams,
        searchText: debouncedSearchTerm,
        requestedFromPage: "playstyles-lab",
        playStylesLab: true,
      }),
    );
  }, [
    dispatch,
    page,
    playerRole,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
    stableQueryParams,
    sort,
  ]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      if (sortBeforeSearchRef.current === null && sort === "evo-popularity") {
        sortBeforeSearchRef.current = sort;
        setSort("meta-rating");
      }
    } else {
      if (sortBeforeSearchRef.current !== null) {
        setSort(sortBeforeSearchRef.current);
        sortBeforeSearchRef.current = null;
      }
    }
  }, [debouncedSearchTerm, sort]);

  useEffect(() => {
    if (requestedFromPage !== "playstyles-lab") {
      dispatch(clearPlayers());
    }
  }, []);

  const handleToolbarAction = (action: ToolbarAction) => {
    setElementOpened(elementOpened === action ? "none" : action);
  };

  const handleSelectPlayerRole = (role: PlayerRole) => {
    setPage(1);
    setPlayerRole(role);
  };

  const handleSearch = (term: string) => {
    if (sort === "date-added" && term.length > 2) {
      setPage(1);
      setSort("meta-rating");
    }
    setSearchTerm(term);
  };

  const [view, setView] = usePlayersView();
  const handleMyClubClicked = useMyClubFilter(
    (f) => {
      dispatch(updateFilter(f));
    },
    {
      onFilterApplied: (type) =>
        trackQuickFilter({
          subsection: "PlayStyles Lab",
          filters: quickFilterKeyFromType(type),
        }),
      origin: { section: "PlayStyles Lab", subsection: "Player Picker" },
      upgradeTracking: {
        section: "PlayStyles Lab",
        subsection: "Player Picker",
        context: "filter",
      },
    },
  );

  const handleChoosePlayStylesLabPlayer = (player: MetaRatingsPlayer) => {
    dispatch(addPlayerToPlayStylesLab({ player }));
    setSearchTerm("");
  };

  // Top of the playstyles-lab hierarchy, entered from the nav on any page, so
  // back returns to whichever one it was.
  const goBack = useGoBack({ fallback: "/" });

  const onFiltersUpdated = () => {
    const filters = stableQueryParams
      .map((qp) => Object.keys(qp ?? {})[0])
      .filter(Boolean);
    trackFilter({
      subsection: "PlayStyles Lab",
      filters,
    });
  };

  if (resourceId) {
    return <Outlet context={currentPlayStylesLabPlayer} />;
  }

  return (
    <main className="min-h-screen mx-auto bg-background">
      <HeroSection
        helmetTitle={t("playStylesLab.picker.helmetTitle", { game: GAME_NAME })}
        pageTitle={t("playStylesLab.picker.helmetTitle", { game: GAME_NAME })}
        helmetDescription={t("playStylesLab.picker.helmetDescription", {
          game: GAME_NAME,
        })}
        subtitle={t("playStylesLab.picker.subtitle")}
        helmetCanonicalLink={rootCanonicalUrl("/playstyles-lab")}
        back={goBack}
        shouldBeInTheDocumentFlow
      />
      <div ref={refTopBar} />
      <PlayersToolbar
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        hasFilters={hasFilters}
        sortBy={sort}
        setSortBy={setSort}
        playerRole={playerRole}
        pageType="players"
        isEvolutionsBuilderContext={true}
        viewToggle={<PlayersViewToggle value={view} onChange={setView} />}
        quickFilters={
          <QuickFilters
            onMyClubClicked={handleMyClubClicked}
            onFiltersChanged={() => {}}
          />
        }
      />

      <div className="max-w-content mx-auto">
        <EvolutionsPlayersSection
          onFiltersUpdated={onFiltersUpdated}
          elementOpened={elementOpened}
          playersStatus={playersStatus}
          players={players}
          isTouchDevice={isTouchDevice}
          selectedPlayerRoles={playerRole ? [playerRole] : []}
          playerFilters={playerFilters}
          scrollPastTopBar={scrollPastTopBar}
          handleToolbarAction={handleToolbarAction}
          hasFilters={hasFilters}
          playerRoles={playerRoles}
          handleSelectPlayerRole={handleSelectPlayerRole}
          multiSelectPlayerRoles={false}
          refPlayerRole={refPlayerRole}
          refFilters={refFilters}
          handleMyClubClicked={handleMyClubClicked}
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          sortBy="meta-rating"
          view={view}
          basePath="/playstyles-lab"
          onSelectPlayer={handleChoosePlayStylesLabPlayer}
          searchTermErrorMessage={searchTermErrorMessage}
        />
      </div>
    </main>
  );
};
